import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, ElementRef, HostListener, ViewChild, inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, firstValueFrom } from 'rxjs';

import { ApiMethod, AuthEndPoints } from '../../../helper/apiconstant';
import { DEFAULT_DASHBOARD_USER } from '../../dashboard/dashboard.data';
import { ClickOutsideDirective } from '../../../helper/directives/click-outside.directive';
import { AppPreferencesService } from '../../../services/app-preferences';
import { Httpservice } from '../../../services/httpservice';
import { ManageGeneratorAutocompleteComponent, ManageGeneratorAutocompleteCompleteEvent } from '../../master/manage-generator/manage-generator-autocomplete';
import { SocketService, SocketServerEvent } from '../../../services/socket.service';
import { ServerTimeService } from '../../../services/server-time';
import { ReportShellComponent } from '../../structure/report-shell/report-shell';
import { getTodayDateValue, resolvePeriodForBlockLabel } from './log-sheet-report.data';
import {
  applyPreviousValueMode,
  buildReportBlockGroups,
  commitReportAutocompleteValue,
  buildEditDataFormValue,
  clearTruthyFormControls,
  downloadReportBlob,
  extractReportBsCalendar,
  extractReportDateConversion,
  filterReportOptions,
  findReportOptionByLabel,
  buildGroupedReportHierarchyNodes,
  buildLinkedReportHierarchyNodes,
  flattenReportHierarchyNodes,
  formatReportNumber,
  getReportCellText,
  getNullableNumberValue,
  getReportDateLabel,
  getReportHierarchyNodeOrderKey,
  getReportRowBlockExtremeValue,
  getDefaultReportDate,
  getReportDatePreferenceKey,
  normalizeReportShowValues,
  ReportDataParsingBase,
  resetReportEditForm,
  extractReportSummary,
  extractReportGeneratorOptions,
  extractReportBlockDefinitions,
  isMissingReportHierarchyLabel,
  normalizeReportHierarchyLabel,
  normalizeOptionalReportHierarchyLabel,
  isFuelReportHierarchyLabel,
  resolveReportBlockPeriod,
  extractReportRows,
  initializeReportPage,
  bindReportFilterListeners,
  listenToReportPreferenceChanges,
  captureReportViewState,
  restoreReportViewState,
  isCurrentBsReportDateSelected,
  isCurrentAdReportDateSelected,
  handleReportEditDataValueInput,
  openReportDatePicker,
  loadReportBsCalendar,
  handleReportDateSelection,
  buildReportExportFileName,
  getReportSelectedDateValue,
  getReportSelectedDateFormValue,
  updateReportCellValue,
  submitReportEdit,
  refreshReportGeneratorOptions,
  patchReportPreviousValue,
  cloneReportRowWithBlocks,
  aggregateReportRows,
  listenToReportSocketEvents,
  loadReportGeneratorOptions,
  handleReportAutocompleteSearch,
  hasTextValue,
  isLogsheetDataDetailCell,
  shouldFetchPreviousValue,
  stripTrailingCapacityLabel,
  toggleReportHierarchyRow,
  sortReportGeneratorNodesByInstalledCapacity,
} from '../shared/report-ui.helpers';
import {
  LogSheetBlockCell,
  LogSheetBlockDefinition,
  LogSheetBsCalendarCell,
  LogSheetBsCalendarResponse,
  LogSheetPreviousBlockValueResponse,
  LogSheetReportResponse,
  LogSheetRow,
  LogSheetSummary,
  LogSheetSubstationOption,
  LogSheetTimeIntervalOption,
} from './log-sheet-report.interfaces';

interface LogSheetHierarchyNode {
  row: LogSheetRow;
  children: LogSheetHierarchyNode[];
}

interface LogSheetBlockGroup {
  period: LogSheetBlockDefinition['period'];
  label: string;
  span: number;
}

type LogSheetViewTab = 'generation' | 'summary';
type LogSheetDisplayMode = 'generation' | 'spill' | 'water-level' | 'frequency-voltage' | 'interruption' | 'summary';

const FIRST_COLUMN_HEADER_LABELS: Record<LogSheetDisplayMode, string> = {
  generation: 'Parameter / Hour',
  interruption: 'Parameter / Hour',
  spill: 'Generator / Hour',
  'water-level': 'Generator / Hour',
  'frequency-voltage': 'Parameter / Hour',
  summary: 'Parameter / Hour',
};
const POWER_ACCORDION_ORDER = [
  'sector:nea',
  'row:power-total-thermal',
  'sector:nea-subsidiary',
  'sector:ipp',
  'row:power-kathmandu-load',
] as const;
const SUMMARY_ACCORDION_ORDER = [
  'sector:nea',
  'row:power-total-thermal',
  'sector:nea-subsidiary',
  'sector:ipp',
  'row:power-total-import',
  'row:power-total-export',
  'row:power-net-import',
  'row:power-system-load-actual',
  'row:power-interruption-tripping',
  'row:power-system-load-expected',
  'row:power-national-load-actual',
  'row:power-line-flows',
  'row:power-frequency-voltage-profile',
  'row:power-frequency-voltage-substation',
  'row:power-kathmandu-load',
  'row:power-spill',
  'row:power-water-level',
] as const;
const POWER_ACCORDION_ORDER_MAP = new Map<string, number>(
  POWER_ACCORDION_ORDER.map((key, index) => [key, index]),
);
const SUMMARY_ACCORDION_ORDER_MAP = new Map<string, number>(
  SUMMARY_ACCORDION_ORDER.map((key, index) => [key, index]),
);

@Component({
  selector: 'app-log-sheet-report-page',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, ReportShellComponent, ManageGeneratorAutocompleteComponent, ClickOutsideDirective],
  templateUrl: './log-sheet-report.html',
  styleUrl: './log-sheet-report.scss',
})
export class LogSheetReportPage extends ReportDataParsingBase implements OnInit {
  protected readonly user = DEFAULT_DASHBOARD_USER;
  protected readonly filtersForm: UntypedFormGroup;

  protected isLoading = false;
  protected isExporting = false;
  protected isSavingCell = false;
  protected selectedViewTab: LogSheetViewTab = 'generation';
  protected detailMode: LogSheetDisplayMode = 'generation';

  protected blockColumns: LogSheetBlockDefinition[] = [];
  protected blockGroups: LogSheetBlockGroup[] = [];
  protected rows: LogSheetRow[] = [];
  protected baseLoad = 0;
  protected summary: LogSheetSummary = {
    scheduled_mwh: 0,
    actual_mwh: 0,
    deviation_mwh: 0,
    row_count: 0,
  };

  protected substationOptions: LogSheetSubstationOption[] = [];
  protected filteredSubstationOptions: LogSheetSubstationOption[] = [];
  protected substationSearchTerm = '';
  protected bsCalendarOpen = false;
  protected bsCalendarLoading = false;
  protected bsCalendarMonth: LogSheetBsCalendarResponse | null = null;
  protected readonly bsCalendarWeekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  protected timeIntervalOptions: LogSheetTimeIntervalOption[] = [
    { value: 60, label: 'Hourly' },
  ];

  protected editDataModalOpen = false;
  protected remarksForm: UntypedFormGroup;
  protected socketId: string | null = null;

  @ViewChild('tableWrap') private readonly tableWrap?: ElementRef<HTMLDivElement>;
  @ViewChild('hiddenDateInput') private readonly hiddenDateInput?: ElementRef<HTMLInputElement>;
  @ViewChild('bsDatePickerShell') private readonly bsDatePickerShell?: ElementRef<HTMLDivElement>;

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private hierarchyRoots: LogSheetHierarchyNode[] = [];
  private reportRowsCache: LogSheetRow[] = [];
  private expandedRowKeys = new Set<string>();
  private pendingViewStateRestore: {
    expandedRowKeys: string[];
    scrollTop: number;
    scrollLeft: number;
  } | null = null;
  private pendingRemarksContext: {
    station_id: number;
    station_code: string | null;
    row_key: string | null;
    row_kind: string | null;
    block_no: number;
    block_value: number | null;
    remarks: string | null;
    data_type?: 'POWER' | 'WATERLEVEL' | 'ENERGY';
  } | null = null;
  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly httpSvc: Httpservice,
    private readonly preferencesSvc: AppPreferencesService,
    private readonly serverTimeSvc: ServerTimeService,
    private readonly socketSvc: SocketService,
  ) {
    super();
    this.filtersForm = this.fb.group({
      date: [getDefaultReportDate(getTodayDateValue()), Validators.required],
      substation_id: [null],
      show_values: [null],
      time_interval: [60],
    });
    this.remarksForm = this.fb.group({
      data_value: [''],
      copy_previous_block_value: [false],
      carry_over_previous_day_values: [false],
      remarks: [''],
    });
  }

  async ngOnInit(): Promise<void> {
    await initializeReportPage({
      applyTemporaryDefaultDate: () => this.applyTemporaryReportDate(this.filtersForm, getTodayDateValue()),
      bindFilterListeners: () => this.bindFilterListeners(),
      loadOptions: () => this.loadSubstationOptions(),
      listenToSocketEvents: () => this.listenToSocketEvents(),
      listenToPreferenceChanges: () => this.listenToPreferenceChanges(),
      handleDateSelectionChange: () => this.handleDateSelectionChange(),
    });
  }

  protected clearFilters(): void {
    this.filtersForm.patchValue(
      {
        date: getDefaultReportDate(getTodayDateValue()),
        substation_id: null,
        show_values: null,
        time_interval: 60,
      },
      { emitEvent: false },
    );
    this.substationSearchTerm = '';
    this.refreshGeneratorOptionsForDetailMode(false);
    this.bsCalendarOpen = false;
    void this.handleDateSelectionChange();
  }

  protected handleExportToExcel(): void {
    if (this.isExporting) {
      return;
    }

    const payload = this.buildReportPayload();
    this.isExporting = true;

    this.httpSvc
      .downloadfile(AuthEndPoints.EXPORT_LOG_SHEET_REPORT, ApiMethod.POST, payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isExporting = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (blob) => downloadReportBlob(blob, buildReportExportFileName(this.getSelectedDateValue())),
      });
  }

  protected trackByBlock(_: number, item: LogSheetBlockDefinition): number {
    return item.block_no;
  }

  protected trackByBlockGroup(_: number, item: LogSheetBlockGroup): string {
    return `${item.period}-${item.span}`;
  }

  protected getBlockTimeHighlightClass(block: LogSheetBlockDefinition | LogSheetBlockCell): string {
    if (!this.isCurrentDateSelected()) {
      return '';
    }

    const currentBlockNo = this.getCurrentTimeBlockNo();
    if (currentBlockNo === null) {
      return '';
    }

    if (block.block_no === currentBlockNo) {
      return 'log-sheet-table__hour-cell--current-block';
    }

    if (block.block_no === this.getPreviousTimeBlockNo(currentBlockNo)) {
      return 'log-sheet-table__hour-cell--previous-block';
    }

    return '';
  }

  protected trackByRow(_: number, item: LogSheetRow): string {
    return item.row_key ?? `${item.row_kind ?? 'data'}-${item.station_id}`;
  }

  protected trackBySubstation(_: number, item: LogSheetSubstationOption): number {
    return item.id;
  }

  protected onSubstationSearchInput(event: ManageGeneratorAutocompleteCompleteEvent): void {
    handleReportAutocompleteSearch({
      query: event?.query,
      form: this.filtersForm,
      controlName: 'substation_id',
      normalizeNumber: (value) => this.normalizeNumber(value),
      filterOptions: (query) => this.filterSubstationAutocomplete(query),
      findOption: (label) => this.findSubstationByLabel(label),
    });
  }

  protected onSubstationSearchBlur(): void {
    this.commitSubstationAutocompleteValue();
  }

  protected getSelectedShowValuesLabel(): string {
    const value = this.filtersForm.get('show_values')?.value;
    const label = typeof value === 'string' && value ? value : 'Select Value';
    return label;
  }

  protected getFilterDateLabel(): string {
    return getReportDateLabel(this.selectedDateDisplayLabel, this.selectedDateRequestValue, this.selectedDateAdValue);
  }

  protected getHeaderSummary(): string {
    const count = this.summary.row_count;
    const blockCount = this.getVisibleBlockColumns().length;
    return `Showing ${count} generator${count === 1 ? '' : 's'} • ${blockCount} generation block${blockCount === 1 ? '' : 's'}`;
  }

  protected get isBsDateMode(): boolean {
    return this.preferencesSvc.showBsDate;
  }

  protected setViewTab(viewTab: LogSheetViewTab): void {
    const shouldResetAccordionState = this.selectedViewTab !== viewTab;
    this.selectedViewTab = viewTab;
    this.pendingViewStateRestore = null;
    this.detailMode = viewTab === 'summary' ? 'summary' : 'generation';
    if (!shouldResetAccordionState || !this.reportRowsCache.length) {
      return;
    }

    if (viewTab === 'summary') {
      this.loadReport(true);
      return;
    }

    const hierarchyRows = this.reportRowsCache.filter((row) => !row.force_flat || this.isPowerNestedRow(row));
    this.hierarchyRoots = this.buildHierarchyNodes(hierarchyRows);
    this.expandedRowKeys = this.getDefaultExpandedRowKeys(this.hierarchyRoots);
    if (this.selectedViewTab === 'summary') {
      this.expandedRowKeys = this.pruneSummaryCollapsedSourceKeys(this.hierarchyRoots, this.expandedRowKeys);
    }
    this.rows = this.buildDisplayRowsWithFlatRows();
  }

  protected handleDetailModeChange(mode: LogSheetDisplayMode): void {
    if (this.detailMode === mode) {
      return;
    }

    this.detailMode = mode;
    if (mode === 'summary') {
      this.selectedViewTab = 'generation';
      this.pendingViewStateRestore = null;
      this.refreshGeneratorOptionsForDetailMode();
      this.loadReport(this.rows.length > 0);
      return;
    }

    if (this.selectedViewTab === 'summary') {
      this.selectedViewTab = 'generation';
      this.pendingViewStateRestore = null;
    }

    this.refreshGeneratorOptionsForDetailMode();
    this.loadReport(this.rows.length > 0);
  }

  protected isDetailModeActive(mode: LogSheetDisplayMode): boolean {
    return this.detailMode === mode;
  }

  protected getCellText(cell: LogSheetBlockCell): string {
    return getReportCellText(cell);
  }

  protected getRowCellText(row: LogSheetRow, cell: LogSheetBlockCell): string {
    if (this.isBlankParentRow(row)) {
      return '';
    }

    const mappedCell = this.getTemporaryMappedCell(row, cell);
    return this.getCellText(mappedCell ?? cell);
  }

  protected isEditedCell(cell: LogSheetBlockCell): boolean {
    return isLogsheetDataDetailCell(cell);
  }

  protected isAdminEditedCell(cell: LogSheetBlockCell): boolean {
    return this.isEditedCell(cell) && cell.editor_role_group === 'admin';
  }

  protected isUserEditedCell(cell: LogSheetBlockCell): boolean {
    return this.isEditedCell(cell) && cell.editor_role_group !== 'admin';
  }

  protected getTotalCellText(value: number): string {
    return this.formatNumber(value);
  }

  protected getRowMaxText(row: LogSheetRow): string {
    if (this.isBlankParentRow(row)) {
      return '';
    }

    return this.formatNumber(this.getRowBlockMaxValue(row));
  }

  protected getRowMinText(row: LogSheetRow): string {
    if (this.isBlankParentRow(row)) {
      return '';
    }

    const summaryMinimum = row.summary_min_value;
    return this.formatNumber(summaryMinimum ?? this.getRowBlockMinValue(row));
  }

  protected getRowEnergyText(row: LogSheetRow): string {
    if (this.isBlankParentRow(row)) {
      return '';
    }

    return row.energy_mwh === null || row.energy_mwh === undefined ? '_' : this.formatNumber(row.energy_mwh);
  }

  protected isEnergyCellEdited(row: LogSheetRow): boolean {
    return row.energy_source === 'logsheet_data_details';
  }

  protected isEnergyEditedByAdmin(row: LogSheetRow): boolean {
    return this.isEnergyCellEdited(row) && row.energy_editor_role_group === 'admin';
  }

  protected isEnergyEditedByUser(row: LogSheetRow): boolean {
    return this.isEnergyCellEdited(row) && row.energy_editor_role_group !== 'admin';
  }

  protected isEnergyCellInteractive(row: LogSheetRow): boolean {
    return (
      this.isSummaryMode() &&
      !this.isBlankParentRow(row) &&
      !this.isFrontendAggregateEnergyRow(row) &&
      this.isYesterdayDateSelected() &&
      !this.isLoading &&
      !this.isExporting &&
      !this.isSavingCell &&
      !this.editDataModalOpen
    );
  }

  protected isFrontendAggregateEnergyRow(row: LogSheetRow): boolean {
    return row.row_kind === 'sector-total' || row.row_kind === 'source-total' || row.row_kind === 'subcat-total';
  }

  protected getInstalledCapacityText(row: LogSheetRow): string {
    if (this.isBlankParentRow(row)) {
      return '';
    }

    return this.formatNumber(row.installed_capacity ?? 0);
  }

  protected getBaseLoadText(): string {
    return this.formatNumber(this.baseLoad);
  }

  protected getFirstColumnHeaderLabel(): string {
    return FIRST_COLUMN_HEADER_LABELS[this.detailMode];
  }

  protected getReportUnitLabel(): string {
    return this.detailMode === 'water-level' ? 'Meter (M)' : 'MW';
  }

  protected isSummaryMode(): boolean {
    return this.detailMode === 'summary';
  }

  protected getTableRows(): LogSheetRow[] {
    if (this.detailMode !== 'water-level') {
      return this.rows;
    }

    return this.rows.filter((row) => row.row_kind === 'data' || row.row_kind === 'power-water-level-generator');
  }

  protected getVisibleBlockColumns(): LogSheetBlockDefinition[] {
    if (!this.isCurrentDateSelected()) {
      return this.blockColumns;
    }

    const currentBlockNo = this.getCurrentTimeBlockNo();
    const currentBlockMinutes = currentBlockNo === null ? null : this.getBlockMinutesByNo(currentBlockNo);
    if (currentBlockMinutes === null) {
      return this.blockColumns;
    }

    const visibleBlockNos = new Set(
      this.getBlocksWithMinutes()
        .filter((item) => item.minutes <= currentBlockMinutes)
        .map((item) => item.block.block_no),
    );

    return this.blockColumns.filter((block) => visibleBlockNos.has(block.block_no));
  }

  protected getVisibleBlockGroups(): LogSheetBlockGroup[] {
    return this.buildBlockGroups(this.getVisibleBlockColumns());
  }

  protected getVisibleRowBlocks(row: LogSheetRow): LogSheetBlockCell[] {
    const visibleBlockNos = new Set(this.getVisibleBlockColumns().map((block) => block.block_no));
    return row.blocks.filter((cell) => visibleBlockNos.has(cell.block_no));
  }

  protected isCellInteractive(row: LogSheetRow, cell: LogSheetBlockCell): boolean {
    return (
      this.isEditableLogsheetRow(row) &&
      this.isHourlyTimeIntervalSelected() &&
      !this.isSectorTotalRow(row) &&
      this.isCurrentDateSelected() &&
      !this.isLoading &&
      !this.isExporting &&
      !this.isSavingCell &&
      !this.editDataModalOpen &&
      !this.isTemporaryCurrentBlockCell(cell)
    );
  }

  protected isGeneratorRow(row: LogSheetRow): boolean {
    const rowKind = row.row_kind ?? 'data';
    return rowKind === 'data' || rowKind.endsWith('-generator') || Boolean(row.is_small_power);
  }

  protected isHydroSmallPowerRow(row: LogSheetRow): boolean {
    return Boolean(row.is_small_power) && row.source_category?.trim().toLowerCase() === 'hydro';
  }

  protected isEditableLogsheetRow(row: LogSheetRow): boolean {
    const rowKind = row.row_kind ?? 'data';
    const rowKey = row.row_key ?? '';
    return (
      this.isGeneratorRow(row) ||
      rowKind === 'interruption-line' ||
      rowKind === 'interruption-substation' ||
      rowKind === 'interruption-interruption' ||
      (this.detailMode === 'frequency-voltage' && rowKind === 'data' && rowKey.startsWith('frequency-voltage-substation-'))
    );
  }

  private isHourlyTimeIntervalSelected(): boolean {
    return (this.normalizeNumber(this.filtersForm.get('time_interval')?.value) ?? 60) === 60;
  }

  protected isSectorTotalRow(row: LogSheetRow): boolean {
    return this.isHierarchyTotalRow(row) && !this.isEditableLogsheetRow(row);
  }

  protected isHierarchyTotalRow(row: LogSheetRow): boolean {
    if (
      row.row_kind === 'power-small' ||
      row.row_kind === 'power-thermal-generator' ||
      row.row_kind === 'power-kathmandu-load-generator' ||
      (this.detailMode === 'water-level' && row.row_kind === 'power-water-level-generator')
    ) {
      return false;
    }

    return row.row_kind !== 'data';
  }

  protected hasHierarchyChildren(row: LogSheetRow): boolean {
    if (!row.row_key || !row.has_children) {
      return false;
    }

    if (this.selectedViewTab === 'summary') {
      if (row.row_kind === 'subcat-total') {
        return false;
      }
    }

    return true;
  }

  protected isBlankParentRow(row: LogSheetRow): boolean {
    return (
      row.row_kind === 'interruption-line-flows' ||
      row.row_kind === 'power-line-flows' ||
      row.row_kind === 'frequency-voltage-substation' ||
      row.row_key === 'power-frequency-voltage-profile' ||
      row.row_key === 'power-frequency-voltage-substation'
    );
  }

  protected isHierarchyExpanded(row: LogSheetRow): boolean {
    if (!row.row_key) {
      return false;
    }

    return this.expandedRowKeys.has(row.row_key);
  }

  protected toggleHierarchyRow(row: LogSheetRow): void {
    if (!toggleReportHierarchyRow(row, this.expandedRowKeys)) {
      return;
    }

    this.rows = this.buildRowsFromCurrentState();
  }

  protected beginCellEdit(row: LogSheetRow, cell: LogSheetBlockCell): void {
    if (!this.isCellInteractive(row, cell)) {
      return;
    }

    this.openEditDataModal(row, cell);
  }

  protected beginEnergyEdit(row: LogSheetRow): void {
    if (!this.isEnergyCellInteractive(row)) {
      return;
    }

    this.openEnergyEditDataModal(row);
  }

  protected closeEditDataModal(): void {
    this.editDataModalOpen = false;
    this.pendingRemarksContext = null;
    resetReportEditForm(this.remarksForm);
  }

  protected saveEditData(): void {
    if (!this.pendingRemarksContext) {
      this.closeEditDataModal();
      return;
    }

    const context = { ...this.pendingRemarksContext };
    const formValue = this.remarksForm.getRawValue();
    const remarks = typeof formValue.remarks === 'string' ? formValue.remarks.trim() : '';
    const dataValue = this.normalizeNullableNumber(formValue.data_value);
    const payload = {
      data_date: this.getSelectedDateValue(),
      generator_id: context.station_id,
      station_code: context.station_code,
      row_key: context.row_key,
      row_kind: context.row_kind,
      block_no: context.block_no,
      block_value: dataValue,
      remarks: remarks || null,
      data_type: context.data_type ?? this.resolveLogsheetEditDataType(context.row_kind, context.row_key),
      detail_mode: this.detailMode,
      bs_ad_key: getReportDatePreferenceKey(this.preferencesSvc.showBsDate ? 'BS' : 'AD'),
    };

    if (context.data_type === 'ENERGY') {
      this.updateLocalEnergyValue(context.row_key, dataValue, remarks || null);
    } else {
      this.updateLocalCellValue(context.station_id, context.block_no, dataValue, remarks || null);
    }
    submitReportEdit({
      request$: this.httpSvc.requestCall(AuthEndPoints.UPDATE_LOG_SHEET_CELL, ApiMethod.PATCH, payload, true),
      destroyRef: this.destroyRef,
      setSaving: (saving) => {
        this.isSavingCell = saving;
      },
      detectChanges: () => this.cdr.detectChanges(),
      onSuccess: () => {
        this.closeEditDataModal();
        this.loadReport(true);
      },
      onError: () => this.loadReport(true),
    });
  }

  protected onEditDataValueInput(event: Event): void {
    handleReportEditDataValueInput(event, (controlNames) => {
      this.clearPreviousValueSelections(...controlNames);
    });
  }

  private clearPreviousValueSelections(...controlNames: string[]): void {
    clearTruthyFormControls(this.remarksForm, controlNames);
  }

  protected onPreviousValueModeChange(mode: 'copy' | 'carry'): void {
    applyPreviousValueMode(this.remarksForm, mode);
  }

  protected arePreviousValueControlsDisabled(): boolean {
    const dataValue = this.remarksForm.get('data_value')?.value;
    return this.isEnergyEditContext() || hasTextValue(dataValue);
  }

  protected isEnergyEditContext(): boolean {
    return this.pendingRemarksContext?.data_type === 'ENERGY';
  }

  protected onCopyPreviousBlockValueChange(): void {
    const context = this.pendingRemarksContext;
    if (!shouldFetchPreviousValue(this.remarksForm, 'copy_previous_block_value', 'carry_over_previous_day_values', Boolean(context), this.isEnergyEditContext()) || !context) {
      return;
    }

    this.fetchPreviousBlockValue(context.station_id, context.block_no);
  }

  protected onCarryOverPreviousDayValuesChange(): void {
    const context = this.pendingRemarksContext;
    if (!shouldFetchPreviousValue(this.remarksForm, 'carry_over_previous_day_values', 'copy_previous_block_value', Boolean(context), this.isEnergyEditContext()) || !context) {
      return;
    }

    this.fetchPreviousDayBlockValue(context.station_id, context.block_no);
  }

  protected formatNumber(value: number | null): string {
    return formatReportNumber(value);
  }

  protected getEmptyColspan(): number {
    return this.getVisibleBlockColumns().length + (this.isSummaryMode() ? 5 : 1);
  }

  @HostListener('document:mousedown', ['$event'])
  protected handleDocumentMouseDown(event: MouseEvent): void {
    const datePickerShell = this.bsDatePickerShell?.nativeElement;
    if (this.bsCalendarOpen && datePickerShell && !datePickerShell.contains(event.target as Node)) {
      this.closeBsDatePicker();
    }
  }

  private collapseAllAccordions(): void {
    this.expandedRowKeys.clear();
    this.rows = this.buildDisplayRowsWithFlatRows();
  }

  private bindFilterListeners(): void {
    bindReportFilterListeners(
      this.filtersForm,
      this.destroyRef,
      ['substation_id', 'show_values', 'time_interval'],
      () => this.handleDateSelectionChange(this.rows.length > 0),
      () => this.loadReport(this.rows.length > 0),
      () => this.onCopyPreviousBlockValueChange(),
      () => this.onCarryOverPreviousDayValuesChange(),
    );
  }

  private listenToSocketEvents(): void {
    listenToReportSocketEvents(
      this.socketSvc.events$,
      this.socketSvc.socketId$,
      this.destroyRef,
      (event) => this.handleSocketEvent(event),
      (socketId) => {
        this.socketId = socketId;
        this.cdr.detectChanges();
      },
    );
  }

  private listenToPreferenceChanges(): void {
    listenToReportPreferenceChanges(this.destroyRef, () => {
      this.closeBsDatePicker();
      this.handleDateSelectionChange(this.rows.length > 0);
    });
  }

  private handleSocketEvent(event: SocketServerEvent): void {
    if (event.event === 'scada_sync_completed') {
      this.loadReport(true);
    }
  }

  private loadSubstationOptions(): void {
    loadReportGeneratorOptions(
      this.httpSvc.requestCall(
        AuthEndPoints.GET_GENERATORS_LIST,
        ApiMethod.GET,
        '?sort_by=installed_capacity&sort_order=desc',
        false,
        { suppressToast: true },
      ),
      this.destroyRef,
      (response) => {
        this.substationOptions = this.extractSubstationOptions(response);
        this.refreshGeneratorOptionsForDetailMode();
        this.cdr.detectChanges();
      },
      () => {
        this.substationOptions = [];
        this.filteredSubstationOptions = [];
        this.cdr.detectChanges();
      },
    );
  }

  private filterSubstationAutocomplete(query: string): void {
    const sourceOptions = this.getVisibleGeneratorOptions();
    this.filteredSubstationOptions = filterReportOptions(sourceOptions, query);
  }

  private commitSubstationAutocompleteValue(): void {
    commitReportAutocompleteValue({
      searchTerm: this.substationSearchTerm,
      form: this.filtersForm,
      controlName: 'substation_id',
      normalizeNumber: (value) => this.normalizeNumber(value),
      findOption: (label) => this.findSubstationByLabel(label),
      setSearchTerm: (value) => {
        this.substationSearchTerm = value;
      },
    });
  }

  private findSubstationByLabel(label: string): LogSheetSubstationOption | null {
    return findReportOptionByLabel(this.getVisibleGeneratorOptions(), this.getVisibleGeneratorOptions(), label);
  }

  private loadReport(preserveViewState = false): void {
    if (preserveViewState) {
      this.captureViewStateForRestore();
    } else {
      this.pendingViewStateRestore = null;
    }

    const payload = this.buildReportPayload();
    this.isLoading = true;

    this.httpSvc
      .requestCall(AuthEndPoints.GET_LOG_SHEET_REPORT, ApiMethod.POST, payload, false, { suppressToast: true })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (response) => {
          const report = this.extractReport(response);
          this.applyReport(report);
        },
        error: () => {
          if (preserveViewState) {
            this.restoreViewStateAfterRefresh();
            return;
          }

          this.applyReport(null);
        },
      });
  }

  private applyReport(report: LogSheetReportResponse | null): void {
    if (!report) {
      this.resetReportState();
      return;
    }

    this.blockColumns = report.blocks;
    this.blockGroups = this.buildBlockGroups(report.blocks);
    this.reportRowsCache = this.getDisplayRowsForCurrentMode(report.rows).map((row) => this.cloneReportRow(row));
    const shouldRestoreViewState = Boolean(this.pendingViewStateRestore);
    if (this.detailMode === 'water-level') {
      this.applyWaterLevelReportRows();
      this.applyReportMetadata(report);
      this.pendingViewStateRestore = null;
      return;
    }

    const isFlatReportMode = report.accordion_enabled === false || report.report_mode === 'interruption' || report.report_mode === 'frequency-voltage';
    if (isFlatReportMode) {
      this.applyFlatReportRows(shouldRestoreViewState);
    } else {
      this.applyHierarchyReportRows(shouldRestoreViewState);
    }
    this.applyReportMetadata(report);

    if (shouldRestoreViewState && !isFlatReportMode) {
      this.restoreViewStateAfterRefresh();
    } else {
      this.pendingViewStateRestore = null;
    }
  }

  private resetReportState(): void {
    this.blockColumns = [];
    this.blockGroups = [];
    this.rows = [];
    this.hierarchyRoots = [];
    this.reportRowsCache = [];
    this.expandedRowKeys.clear();
    this.baseLoad = 0;
    this.summary = {
      scheduled_mwh: 0,
      actual_mwh: 0,
      deviation_mwh: 0,
      row_count: 0,
    };
  }

  private applyFlatReportRows(shouldRestoreViewState: boolean): void {
    this.hierarchyRoots = [];
    const flatNodes = this.buildLinkedRowNodes(this.reportRowsCache);
    this.expandedRowKeys = shouldRestoreViewState && this.pendingViewStateRestore
      ? new Set(this.pendingViewStateRestore.expandedRowKeys)
      : this.getDefaultFlatExpandedRowKeys(flatNodes);
    this.rows = this.flattenHierarchyNodes(flatNodes, this.expandedRowKeys);
  }

  private applyHierarchyReportRows(shouldRestoreViewState: boolean): void {
    const hierarchyRows = this.reportRowsCache.filter((row) => !row.force_flat || this.isPowerNestedRow(row));
    this.hierarchyRoots = this.buildHierarchyNodes(hierarchyRows);
    this.expandedRowKeys = shouldRestoreViewState && this.pendingViewStateRestore
      ? new Set(this.pendingViewStateRestore.expandedRowKeys)
      : this.getDefaultExpandedRowKeys(this.hierarchyRoots);
    if (!shouldRestoreViewState && this.selectedViewTab === 'summary') {
      this.expandedRowKeys = this.pruneSummaryCollapsedSourceKeys(this.hierarchyRoots, this.expandedRowKeys);
    }
    if (!shouldRestoreViewState && this.detailMode === 'generation' && this.reportRowsCache.some(
      (row) => row.row_kind === 'power-kathmandu-load' && row.has_children,
    )) {
      this.expandedRowKeys.add('power-kathmandu-load');
    }
    this.rows = this.buildDisplayRowsWithFlatRows();
  }

  private applyReportMetadata(report: LogSheetReportResponse): void {
    this.summary = report.summary;
    this.baseLoad = report.base_load ?? 0;
    this.applyTimeIntervalMetadata(report);
    if (!this.preferencesSvc.showBsDate && report.date_label) {
      this.selectedDateDisplayLabel = report.date_label;
    }
  }

  private applyTimeIntervalMetadata(report: LogSheetReportResponse): void {
    const options = report.time_interval?.options ?? [];
    if (options.length) {
      this.timeIntervalOptions = options;
    }

    const selectedInterval = report.time_interval?.selected ?? report.filters.time_interval ?? null;
    if (selectedInterval !== null && selectedInterval !== undefined && this.filtersForm.get('time_interval')?.value !== selectedInterval) {
      this.filtersForm.patchValue({ time_interval: selectedInterval }, { emitEvent: false });
    }
  }

  private applyWaterLevelReportRows(): void {
    this.hierarchyRoots = [];
    this.expandedRowKeys.clear();
    this.rows = this.reportRowsCache.filter((row) => row.row_kind === 'data' || row.row_kind === 'power-water-level-generator');
  }

  private openEditDataModal(row: LogSheetRow, cell: LogSheetBlockCell): void {
    this.pendingRemarksContext = {
      station_id: row.station_id,
      station_code: row.station_code ?? null,
      row_key: row.row_key ?? null,
      row_kind: row.row_kind ?? null,
      block_no: cell.block_no,
      block_value: getNullableNumberValue(cell.value),
      remarks: cell.remarks ?? null,
      data_type: this.resolveLogsheetEditDataType(row.row_kind, row.row_key),
    };
    this.remarksForm.reset(buildEditDataFormValue(cell.value, cell.remarks), { emitEvent: false });
    this.editDataModalOpen = true;
  }

  private openEnergyEditDataModal(row: LogSheetRow): void {
    this.pendingRemarksContext = {
      station_id: row.station_id,
      station_code: row.station_code ?? null,
      row_key: row.row_key ?? null,
      row_kind: row.row_kind ?? null,
      block_no: 0,
      block_value: getNullableNumberValue(row.energy_mwh),
      remarks: row.energy_remarks ?? null,
      data_type: 'ENERGY',
    };
    this.remarksForm.reset(buildEditDataFormValue(row.energy_mwh, row.energy_remarks), { emitEvent: false });
    this.editDataModalOpen = true;
  }

  private resolveLogsheetEditDataType(
    rowKind: string | null | undefined,
    rowKey: string | null | undefined,
  ): 'POWER' | 'WATERLEVEL' {
    if (
      this.detailMode === 'water-level' ||
      rowKind === 'power-water-level-generator' ||
      (rowKey ?? '').startsWith('power-water-level-generator-')
    ) {
      return 'WATERLEVEL';
    }

    return 'POWER';
  }

  private fetchPreviousBlockValue(stationId: number, blockNo: number): void {
    this.fetchPreviousValue(AuthEndPoints.GET_LOG_SHEET_PREVIOUS_BLOCK_VALUE, stationId, blockNo, 'copy_previous_block_value');
  }

  private fetchPreviousDayBlockValue(stationId: number, blockNo: number): void {
    this.fetchPreviousValue(AuthEndPoints.GET_LOG_SHEET_PREVIOUS_DAY_BLOCK_VALUE, stationId, blockNo, 'carry_over_previous_day_values');
  }

  private fetchPreviousValue(endpoint: string, stationId: number, blockNo: number, resetControlName: string): void {
    const context = this.pendingRemarksContext;
    const payload = {
      data_date: this.getSelectedDateValue(),
      generator_id: stationId,
      station_code: context?.station_code ?? null,
      row_key: context?.row_key ?? null,
      row_kind: context?.row_kind ?? null,
      block_no: blockNo,
      data_type: context?.data_type ?? this.resolveLogsheetEditDataType(context?.row_kind, context?.row_key),
      detail_mode: this.detailMode,
      bs_ad_key: getReportDatePreferenceKey(this.preferencesSvc.showBsDate ? 'BS' : 'AD'),
    };

    this.httpSvc
      .requestCall(endpoint, ApiMethod.POST, payload, false, { suppressToast: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => this.patchPreviousValue(response),
        error: () => this.remarksForm.patchValue({ [resetControlName]: false }, { emitEvent: false }),
      });
  }

  private patchPreviousValue(response: unknown): void {
    const previous = this.extractPreviousBlockValue(response);
    patchReportPreviousValue(this.remarksForm, previous);
  }

  private updateLocalCellValue(stationId: number, blockNo: number, blockValue: number | null, remarks: string | null): void {
    this.reportRowsCache = updateReportCellValue({
      hierarchyRoots: this.hierarchyRoots,
      cachedRows: this.reportRowsCache,
      stationId,
      blockNo,
      blockValue,
      remarks,
      mapCell: (cell) => ({ ...cell, editor_role_name: null, editor_role_group: null }),
    });
    this.rows = this.buildDisplayRowsWithFlatRows();
  }

  private updateLocalEnergyValue(rowKey: string | null, energyValue: number | null, remarks: string | null): void {
    if (!rowKey) {
      return;
    }

    const updateRow = (row: LogSheetRow): LogSheetRow => {
      if (row.row_key !== rowKey) {
        return row;
      }

      return {
        ...row,
        energy_mwh: energyValue,
        energy_remarks: remarks,
        energy_source: 'logsheet_data_details',
      };
    };
    this.rows = this.rows.map(updateRow);
    this.reportRowsCache = this.reportRowsCache.map(updateRow);
  }

  private cloneReportRow(row: LogSheetRow): LogSheetRow {
    return cloneReportRowWithBlocks(row);
  }

  private extractPreviousBlockValue(response: unknown): LogSheetPreviousBlockValueResponse | null {
    const value = this.extractResponseValue(response);
    if (!value) {
      return null;
    }

    return {
      data_value: this.normalizeNullableNumber(value['data_value']),
      remarks: this.extractStringValue(value, 'remarks'),
      previous_block_no: this.normalizeNullableNumber(value['previous_block_no']),
      source: this.extractStringValue(value, 'source'),
    };
  }

  private buildReportPayload(): Record<string, unknown> {
    const detailMode = this.detailMode === 'summary' ? 'summary' : this.detailMode;
    return {
      data_date: this.getSelectedDateValue(),
      entity_id: null,
      generation_id: this.normalizeNumber(this.filtersForm.get('substation_id')?.value),
      show_values: this.normalizeSelectedShowValues(),
      detail_mode: detailMode,
      bs_ad_key: getReportDatePreferenceKey(this.preferencesSvc.showBsDate ? 'BS' : 'AD'),
      selected_view_tab: this.selectedViewTab,
      time_interval: this.normalizeNumber(this.filtersForm.get('time_interval')?.value) ?? 60,
    };
  }

  private normalizeSelectedShowValues(): string | null {
    return normalizeReportShowValues(this.filtersForm.get('show_values')?.value);
  }

  private captureViewStateForRestore(): void {
    this.pendingViewStateRestore = captureReportViewState(this.tableWrap?.nativeElement ?? null, this.expandedRowKeys);
  }

  private restoreViewStateAfterRefresh(): void {
    const snapshot = this.pendingViewStateRestore;
    if (!snapshot || this.detailMode === 'interruption' || this.detailMode === 'frequency-voltage') {
      return;
    }
    restoreReportViewState(snapshot, (expandedRowKeys, viewState) => {
      this.expandedRowKeys = expandedRowKeys;
      this.rows = this.buildDisplayRowsWithFlatRows();

      const tableWrap = this.tableWrap?.nativeElement ?? null;
      if (tableWrap) {
        tableWrap.scrollTop = viewState.scrollTop;
        tableWrap.scrollLeft = viewState.scrollLeft;
      }
    }, () => {
      this.pendingViewStateRestore = null;
    }, () => this.cdr.detectChanges());
  }

  private isCurrentDateSelected(): boolean {
    if (this.preferencesSvc.showBsDate) {
      return isCurrentBsReportDateSelected(this.getSelectedDateValue(), this.serverTimeSvc.getCurrentBsDateValue());
    }
    return isCurrentAdReportDateSelected(this.getSelectedDateValue(), getTodayDateValue());
  }

  private isYesterdayDateSelected(): boolean {
    const referenceDate = this.serverTimeSvc.getCurrentTime() ?? new Date();
    const yesterday = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), referenceDate.getDate() - 1);
    const yesterdayValue = [
      yesterday.getFullYear(),
      String(yesterday.getMonth() + 1).padStart(2, '0'),
      String(yesterday.getDate()).padStart(2, '0'),
    ].join('-');
    return this.selectedDateAdValue === yesterdayValue;
  }

  private getCurrentTimeBlockNo(): number | null {
    const blocksWithMinutes = this.getBlocksWithMinutes();
    if (!blocksWithMinutes.length) {
      return null;
    }

    const referenceDate = this.serverTimeSvc.getCurrentTime() ?? new Date();
    const currentMinutes = referenceDate.getHours() * 60 + referenceDate.getMinutes();
    for (const item of blocksWithMinutes) {
      if (item.minutes > currentMinutes) {
        return item.block.block_no;
      }
    }

    return blocksWithMinutes[blocksWithMinutes.length - 1].block.block_no;
  }

  private isTemporaryCurrentBlockCell(cell: LogSheetBlockDefinition | LogSheetBlockCell): boolean {
    return this.isCurrentDateSelected() && cell.block_no === this.getCurrentTimeBlockNo();
  }

  private getTemporaryMappedCell(row: LogSheetRow, cell: LogSheetBlockCell): LogSheetBlockCell | null {
    if (!this.isTemporaryCurrentBlockCell(cell)) {
      return null;
    }

    const previousBlockNo = this.getPreviousTimeBlockNo(cell.block_no);
    if (previousBlockNo === null) {
      return null;
    }

    return row.blocks.find((item) => item.block_no === previousBlockNo) ?? null;
  }

  private getPreviousTimeBlockNo(currentBlockNo: number): number | null {
    const blockNos = this.getBlocksWithMinutes().map((item) => item.block.block_no);
    const currentIndex = blockNos.indexOf(currentBlockNo);
    return currentIndex > 0 ? blockNos[currentIndex - 1] : null;
  }

  private getBlocksWithMinutes(): { block: LogSheetBlockDefinition; minutes: number }[] {
    return this.blockColumns
      .map((block) => {
        const minutes = this.getBlockLabelMinutes(block.label);
        return minutes === null ? null : { block, minutes };
      })
      .filter((item): item is { block: LogSheetBlockDefinition; minutes: number } => item !== null)
      .sort((first, second) => first.minutes - second.minutes);
  }

  private getBlockMinutesByNo(blockNo: number): number | null {
    return this.getBlocksWithMinutes().find((item) => item.block.block_no === blockNo)?.minutes ?? null;
  }

  private getBlockLabelMinutes(label: string | null | undefined): number | null {
    const match = /^(\d{1,2}):(\d{2})/.exec(String(label ?? '').trim());
    if (!match) {
      return null;
    }

    return Number(match[1]) * 60 + Number(match[2]);
  }

  private buildHierarchyNodes(rows: LogSheetRow[]): LogSheetHierarchyNode[] {
    const normalizedRows: LogSheetRow[] = rows.map((row) => ({
      ...row,
      row_kind: this.isPowerNestedRow(row) || row.parent_row_key ? row.row_kind : 'data',
      row_level: this.isPowerNestedRow(row) || row.parent_row_key ? row.row_level : 3,
      row_key: this.isPowerNestedRow(row) || row.parent_row_key ? row.row_key : `data-${row.station_id}`,
      has_children: false,
    }));

    const specialRows = normalizedRows.filter((row) => this.isPowerNestedRow(row));
    const hierarchyRows = normalizedRows.filter((row) => !this.isPowerNestedRow(row));
    const hierarchyNodes = buildGroupedReportHierarchyNodes({
      rows: hierarchyRows,
      isSummaryView: this.selectedViewTab === 'summary',
      normalizeHierarchyLabel: (value) => this.normalizeHierarchyLabel(value),
      normalizeOptionalHierarchyLabel: (value) => this.normalizeOptionalHierarchyLabel(value),
      isFuelHierarchyLabel: (value) => this.isFuelHierarchyLabel(value),
      isMissingHierarchyLabel: (value) => this.isMissingHierarchyLabel(value),
      sortNodes: (nodes) => this.sortHierarchyNodesForCurrentMode(nodes),
      buildAggregateRow: (rowKey, label, aggregateRows, rowLevel, rowKind, hasChildren, forceExpandChildren) =>
        this.buildAggregateRow(rowKey, label, aggregateRows, rowLevel, rowKind, hasChildren, forceExpandChildren),
    });

    return this.attachSpecialPowerRows(hierarchyNodes, specialRows);
  }

  private attachSpecialPowerRows(nodes: LogSheetHierarchyNode[], specialRows: LogSheetRow[]): LogSheetHierarchyNode[] {
    const smallRows = specialRows.filter((row) => row.row_kind === 'power-small');
    const thermalRow = specialRows.find((row) => row.row_key === 'power-total-thermal');
    const thermalChildren = specialRows.filter((row) => row.row_kind === 'power-thermal-generator');
    const remainingNodes = [...nodes];

    for (const row of smallRows) {
      const ownerNode = this.findSectorNode(remainingNodes, row.owner);
      if (!ownerNode) {
        remainingNodes.push({ row, children: [] });
        continue;
      }

      const hydroNode = ownerNode.children.find(
        (node) => node.row.row_kind === 'source-total' && node.row.station_name.toLowerCase().startsWith('total hydro'),
      );
      if (!hydroNode) {
        remainingNodes.push({ row, children: [] });
        continue;
      }

      hydroNode.row.has_children = true;
      const lastFuelRowIndex = hydroNode.children.reduce(
        (lastIndex, child, index) => child.row.row_kind === 'subcat-total' ? index : lastIndex,
        -1,
      );
      hydroNode.children.splice(lastFuelRowIndex + 1, 0, { row: { ...row, row_level: 3 }, children: [] });
    }

    if (thermalRow) {
      const neaNode = this.findSectorNode(remainingNodes, 'NEA');
      if (neaNode) {
        const totalInstalledCapacity = Number(
          ((neaNode.row.installed_capacity ?? 0) + (thermalRow.installed_capacity ?? 0)).toFixed(5),
        );
        const totalLabel = stripTrailingCapacityLabel(neaNode.row.station_name);
        neaNode.row.installed_capacity = totalInstalledCapacity;
        neaNode.row.station_name = `${totalLabel} (${this.formatNumber(totalInstalledCapacity)})`;
        const solarIndex = neaNode.children.findIndex(
          (node) => node.row.row_kind === 'source-total' && node.row.station_name.toLowerCase().startsWith('total solar'),
        );
        const thermalDisplayRow = {
          ...thermalRow,
          station_name: `${thermalRow.station_name} (${this.formatNumber(thermalRow.installed_capacity ?? 0)})`,
          row_level: 2,
          has_children: thermalChildren.length > 0,
        };
        neaNode.children.splice(solarIndex < 0 ? neaNode.children.length : solarIndex + 1, 0, {
          row: thermalDisplayRow,
          children: thermalChildren.map((row) => ({ row: { ...row, row_level: 3 }, children: [] })),
        });
      } else {
        remainingNodes.push({
          row: {
            ...thermalRow,
            station_name: `${thermalRow.station_name} (${this.formatNumber(thermalRow.installed_capacity ?? 0)})`,
            row_level: 2,
          },
          children: thermalChildren.map((row) => ({ row: { ...row, row_level: 3 }, children: [] })),
        });
      }
    }

    return remainingNodes;
  }

  private findSectorNode(nodes: LogSheetHierarchyNode[], owner: string | null | undefined): LogSheetHierarchyNode | null {
    const normalizedOwner = (owner ?? '').trim().toLowerCase();
    for (const node of nodes) {
      const stationLabel = stripTrailingCapacityLabel(node.row.station_name).toLowerCase();
      if (node.row.row_kind === 'sector-total' && stationLabel === `total ${normalizedOwner}`) {
        return node;
      }
      const nestedNode = this.findSectorNode(node.children, owner);
      if (nestedNode) {
        return nestedNode;
      }
    }
    return null;
  }

  private isPowerNestedRow(row: LogSheetRow): boolean {
    return row.row_kind === 'power-small'
      || row.row_kind === 'power-thermal-generator'
      || row.row_key === 'power-total-thermal';
  }

  private buildDisplayRowsWithFlatRows(): LogSheetRow[] {
    const flatNodes = this.sortHierarchyNodesForCurrentMode(
      this.buildLinkedRowNodes(this.reportRowsCache.filter((row) => row.force_flat && !this.isPowerNestedRow(row))),
    );
    return [
      ...this.flattenHierarchyNodes(this.hierarchyRoots, this.expandedRowKeys),
      ...this.flattenHierarchyNodes(flatNodes, this.expandedRowKeys),
    ];
  }

  private buildRowsFromCurrentState(): LogSheetRow[] {
    if (!this.hierarchyRoots.length && this.reportRowsCache.length) {
      return this.flattenHierarchyNodes(this.buildLinkedRowNodes(this.reportRowsCache), this.expandedRowKeys);
    }

    return this.buildDisplayRowsWithFlatRows();
  }

  private getDisplayRowsForCurrentMode(rows: LogSheetRow[]): LogSheetRow[] {
    if (this.detailMode !== 'generation') {
      return rows;
    }

    const visiblePowerFooterRows = new Set([
      'power-total-thermal',
      'power-thermal-generator',
      'power-kathmandu-load',
      'power-kathmandu-load-generator',
      'power-spill',
    ]);
    return rows.filter((row) => !row.force_flat || visiblePowerFooterRows.has(row.row_kind ?? ''));
  }

  private getDefaultFlatExpandedRowKeys(nodes: LogSheetHierarchyNode[]): Set<string> {
    const expandedKeys = new Set<string>();

    for (const node of nodes) {
      if (node.row.has_children && node.row.row_key) {
        expandedKeys.add(node.row.row_key);
      }
    }

    return expandedKeys;
  }

  private buildLinkedRowNodes(rows: LogSheetRow[]): LogSheetHierarchyNode[] {
    return buildLinkedReportHierarchyNodes(rows, (nodes) => this.sortHierarchyNodesForCurrentMode(nodes));
  }

  private sortHierarchyNodesForCurrentMode(nodes: LogSheetHierarchyNode[]): LogSheetHierarchyNode[] {
    const orderMap = this.detailMode === 'summary' ? SUMMARY_ACCORDION_ORDER_MAP : POWER_ACCORDION_ORDER_MAP;
    const orderedNodes = [...nodes].sort((left, right) => this.getHierarchyNodeOrder(left, orderMap) - this.getHierarchyNodeOrder(right, orderMap));
    return sortReportGeneratorNodesByInstalledCapacity(orderedNodes);
  }

  private getHierarchyNodeOrder(node: LogSheetHierarchyNode, orderMap: Map<string, number>): number {
    const key = this.getHierarchyNodeOrderKey(node.row);
    return orderMap.get(key) ?? orderMap.size + 100;
  }

  private getHierarchyNodeOrderKey(row: LogSheetRow): string {
    return getReportHierarchyNodeOrderKey(row);
  }

  private buildAggregateRow(
    rowKey: string,
    label: string,
    rows: LogSheetRow[],
    rowLevel: number,
    rowKind: 'sector-total' | 'ipp-total' | 'source-total' | 'subcat-total',
    hasChildren: boolean,
    forceExpandChildren = false,
  ): LogSheetRow {
    const aggregate = aggregateReportRows(rows, this.blockColumns);
    const installedCapacityLabel = this.formatNumber(aggregate.installed_capacity);
    const blocks = aggregate.blocks.map((block) => ({
      ...block,
      remarks: null,
      source: null,
      editable_id: null,
      editor_role_name: null,
      editor_role_group: null,
    }));

    return {
      station_id: 0,
      station_name: `${label} (${installedCapacityLabel})`,
      station_code: null,
      owner: null,
      source_category: null,
      sub_category: null,
      blocks,
      scheduled_mwh: aggregate.scheduled_mwh,
      actual_mwh: aggregate.actual_mwh,
      deviation_mwh: aggregate.deviation_mwh,
      energy_mwh: aggregate.energy_mwh,
      energy_source: null,
      energy_remarks: null,
      energy_editable_id: null,
      installed_capacity: aggregate.installed_capacity,
      row_key: rowKey,
      row_kind: rowKind,
      row_level: rowLevel,
      has_children: hasChildren,
      force_expand_children: forceExpandChildren,
    };
  }

  private getRowBlockMaxValue(row: LogSheetRow): number | null {
    return getReportRowBlockExtremeValue(row, 'max');
  }

  private getRowBlockMinValue(row: LogSheetRow): number | null {
    return getReportRowBlockExtremeValue(row, 'min');
  }

  private flattenHierarchyNodes(nodes: LogSheetHierarchyNode[], expandedKeys: Set<string>): LogSheetRow[] {
    return flattenReportHierarchyNodes(
      nodes,
      expandedKeys,
      (node) => this.selectedViewTab === 'summary' && Boolean(node.row.force_expand_children),
    );
  }

  private getDefaultExpandedRowKeys(nodes: LogSheetHierarchyNode[]): Set<string> {
    const expandedKeys = new Set<string>();

    for (const node of nodes) {
      if (this.selectedViewTab === 'summary') {
        this.addSummaryExpandedRowKey(node, expandedKeys);
        continue;
      }

      if (!node.row.has_children || !node.row.row_key) {
        continue;
      }

      if ((node.row.row_level ?? 0) <= 1) {
        expandedKeys.add(node.row.row_key);
      }

      for (const child of node.children) {
        if (child.row.row_key && this.shouldExpandPowerModeChild(child)) {
          expandedKeys.add(child.row.row_key);
        }
      }
    }

    return expandedKeys;
  }

  private addSummaryExpandedRowKey(node: LogSheetHierarchyNode, expandedKeys: Set<string>): void {
    if (!node.row.has_children || !node.row.row_key) {
      return;
    }

    if (node.row.row_kind === 'sector-total') {
      expandedKeys.add(node.row.row_key);
      return;
    }

    if (node.row.row_kind === 'source-total' && node.row.force_expand_children) {
      expandedKeys.add(node.row.row_key);
    }
  }

  private shouldExpandPowerModeChild(child: LogSheetHierarchyNode): boolean {
    return Boolean(
      child.row.has_children &&
      child.row.row_key &&
      (child.row.row_level ?? 0) <= 1 &&
      this.hasSubcategoryChildren(child),
    );
  }

  private hasSubcategoryChildren(node: LogSheetHierarchyNode): boolean {
    return node.children.some((grandchild) => grandchild.row.row_kind === 'subcat-total');
  }

  private pruneSummaryCollapsedSourceKeys(nodes: LogSheetHierarchyNode[], expandedKeys: Set<string>): Set<string> {
    const prunedKeys = new Set(expandedKeys);

    const visit = (node: LogSheetHierarchyNode): void => {
      if (
        node.row.row_kind === 'source-total' &&
        node.row.row_key &&
        node.children.length > 0 &&
        node.children.every((child) => !child.row.has_children)
      ) {
        prunedKeys.delete(node.row.row_key);
      }

      for (const child of node.children) {
        visit(child);
      }
    };

    for (const node of nodes) {
      visit(node);
    }

    return prunedKeys;
  }

  private normalizeHierarchyLabel(value: string | null | undefined): string {
    return normalizeReportHierarchyLabel(value, true);
  }

  private normalizeOptionalHierarchyLabel(value: string | null | undefined): string | null {
    return normalizeOptionalReportHierarchyLabel(value);
  }

  private isFuelHierarchyLabel(value: string | null | undefined): boolean {
    return isFuelReportHierarchyLabel(value);
  }

  private isMissingHierarchyLabel(value: string | null | undefined): boolean {
    return isMissingReportHierarchyLabel(value);
  }

  private buildBlockGroups(blocks: LogSheetBlockDefinition[]): LogSheetBlockGroup[] {
    return buildReportBlockGroups(blocks, (period) => this.getPeriodTitle(period));
  }

  private getPeriodTitle(period: LogSheetBlockDefinition['period']): string {
    switch (period) {
      case 'morning':
        return 'Morning (00:00-07:30)';
      case 'day':
        return 'Day (08:00-15:00)';
      case 'night':
        return 'Night (16:00-23:00)';
      default:
        return 'Blocks';
    }
  }

  private resolveBlockPeriod(period: string | null | undefined, label: string | null | undefined): LogSheetBlockDefinition['period'] {
    return resolveReportBlockPeriod(period, label, resolvePeriodForBlockLabel);
  }

  private extractReport(response: unknown): LogSheetReportResponse | null {
    const value = this.extractResponseValue(response);
    if (!value) {
      return null;
    }

    const blocks = this.extractBlockDefinitions(value);
    const rows = this.extractRows(value);
    const summary = this.extractSummary(value);
    const dataDate = this.extractStringValue(value, 'data_date') ?? this.getSelectedDateValue();
    const dateLabel = this.preferencesSvc.formatDateOnly(dataDate);
    const accordionEnabled = this.extractBooleanValue(value, 'accordion_enabled');

    return {
      data_date: dataDate,
      date_label: dateLabel,
      report_mode: this.extractStringValue(value, 'report_mode'),
      accordion_enabled: accordionEnabled,
      blocks,
      rows,
      summary,
      base_load: this.normalizeNumber(this.getRecordValue(value, 'base_load')) ?? 0,
      time_interval: this.extractTimeIntervalMetadata(value),
      filters: {
        entity_id: null,
        substation_id: this.normalizeNumber(this.filtersForm.get('substation_id')?.value),
        time_interval: this.normalizeNumber(this.getRecordValue(this.getRecordValue(value, 'filters'), 'time_interval')),
      },
    };
  }

  private extractTimeIntervalMetadata(value: unknown): LogSheetReportResponse['time_interval'] {
    const record = this.getRecord(this.getRecordValue(value, 'time_interval'));
    const rawOptions = this.getArrayValue(record, 'options');
    const options = rawOptions.map((item) => {
      const optionRecord = this.getRecord(item);
      return {
        value: this.normalizeNumber(optionRecord['value']) ?? 60,
        label: this.extractStringValue(optionRecord, 'label') ?? 'Hourly',
      };
    });
    return {
      selected: this.normalizeNumber(record['selected']) ?? 60,
      options: options.length ? options : [{ value: 60, label: 'Hourly' }],
    };
  }

  private extractBlockDefinitions(value: unknown): LogSheetBlockDefinition[] {
    return extractReportBlockDefinitions(
      value,
      (source, key) => this.getArrayValue(source, key),
      (source) => this.getRecord(source),
      (source) => this.normalizeNumber(source),
      (source, key) => this.extractStringValue(source, key),
      (period, label) => this.resolveBlockPeriod(period, label),
      (_record, block) => block,
    );
  }

  private extractRows(value: unknown): LogSheetRow[] {
    return extractReportRows(
      value,
      {
        getArray: (source, key) => this.getArrayValue(source, key),
        getRecord: (source) => this.getRecord(source),
        normalizeNumber: (source) => this.normalizeNumber(source),
        extractString: (source, key) => this.extractStringValue(source, key),
        resolvePeriod: (period, label) => this.resolveBlockPeriod(period, label),
        mapBlock: (record, block) => ({
          ...block,
          source: block.source as LogSheetBlockCell['source'],
          editor_role_name: this.extractStringValue(record, 'editor_role_name'),
          editor_role_group: this.extractStringValue(record, 'editor_role_group') as LogSheetBlockCell['editor_role_group'],
        }),
        mapRow: (record, row) => ({
          ...row,
          energy_mwh: this.normalizeNullableNumber(record['energy_mwh']),
          energy_remarks: this.extractStringValue(record, 'energy_remarks'),
          energy_source: this.extractStringValue(record, 'energy_source') as LogSheetRow['energy_source'],
          energy_editable_id: this.normalizeNullableNumber(record['energy_editable_id']),
          energy_editor_role_name: this.extractStringValue(record, 'energy_editor_role_name'),
          energy_editor_role_group: this.extractStringValue(record, 'energy_editor_role_group') as LogSheetRow['energy_editor_role_group'],
          installed_capacity: this.normalizeNullableNumber(record['installed_capacity']),
          summary_min_value: this.normalizeNullableNumber(record['summary_min_value']),
          parent_row_key: this.extractStringValue(record, 'parent_row_key'),
          is_small_power: Boolean(record['is_small_power']),
        }),
      },
    );
  }

  private extractSummary(value: unknown): LogSheetSummary {
    return extractReportSummary(value, (source, key) => this.getRecordValue(source, key), (source) => this.getRecord(source), (source) => this.normalizeNumber(source));
  }

  private extractSubstationOptions(response: unknown): LogSheetSubstationOption[] {
    return extractReportGeneratorOptions(
      response,
      (source) => this.extractResponseValue(source),
      (source, key) => this.getArrayValue(source, key),
      (source) => this.getRecord(source),
      (source, key) => this.extractStringValue(source, key),
      (source) => this.normalizeNumber(source),
    );
  }

  private refreshGeneratorOptionsForDetailMode(clearInvalidSelection = true): void {
    const visibleOptions = this.getVisibleGeneratorOptions();
    refreshReportGeneratorOptions({
      visibleOptions,
      isDetailMode: this.isWaterLevelDetailMode(),
      selectedId: this.normalizeNumber(this.filtersForm.get('substation_id')?.value),
      clearInvalidSelection,
      setFilteredOptions: (options) => {
        this.filteredSubstationOptions = options;
      },
      getOptionId: (option) => option.id,
      getOptionName: (option) => option.name,
      setSearchTerm: (value) => {
        this.substationSearchTerm = value;
      },
      clearSelection: () => this.filtersForm.patchValue({ substation_id: null }, { emitEvent: false }),
    });
  }

  private getVisibleGeneratorOptions(): LogSheetSubstationOption[] {
    if (!this.isWaterLevelDetailMode()) {
      return [...this.substationOptions];
    }

    return this.substationOptions.filter(
      (option) => this.hasWaterLevelParameter(option.other_parameter),
    );
  }

  private hasWaterLevelParameter(otherParameter: string | null | undefined): boolean {
    const normalizedValue = typeof otherParameter === 'string' ? otherParameter.trim().toLowerCase() : '';
    return /water\s*level/.test(normalizedValue);
  }

  private isWaterLevelDetailMode(): boolean {
    return this.detailMode === 'water-level';
  }

  private getSelectedDateValue(): string {
    return getReportSelectedDateValue(this.selectedDateRequestValue, this.filtersForm, getTodayDateValue());
  }

  private getSelectedDateFormValue(): string {
    return getReportSelectedDateFormValue(this.filtersForm, getTodayDateValue());
  }

  protected openDatePicker(): void {
    openReportDatePicker(
      this.isBsDateMode,
      this.bsCalendarOpen,
      () => this.closeBsDatePicker(),
      () => void this.openBsDatePicker(),
      this.hiddenDateInput?.nativeElement ?? null,
    );
  }

  protected closeBsDatePicker(): void {
    this.bsCalendarOpen = false;
  }

  protected async selectBsCalendarCell(cell: LogSheetBsCalendarCell): Promise<void> {
    if (!cell?.ad_date) {
      return;
    }

    this.filtersForm.patchValue({ date: cell.ad_date }, { emitEvent: false });
    await this.handleDateSelectionChange(this.rows.length > 0);
    this.closeBsDatePicker();
    this.cdr.detectChanges();
  }

  protected shiftBsCalendarMonth(offset: number): void {
    if (!this.bsCalendarMonth || !Number.isFinite(offset) || !offset) {
      return;
    }

    const nextMonth = this.bsCalendarMonth.bs_month + offset;
    let nextYear = this.bsCalendarMonth.bs_year;
    let normalizedMonth = nextMonth;

    if (nextMonth < 1) {
      normalizedMonth = 12;
      nextYear -= 1;
    } else if (nextMonth > 12) {
      normalizedMonth = 1;
      nextYear += 1;
    }

    this.loadBsCalendar(`${nextYear.toString().padStart(4, '0')}-${normalizedMonth.toString().padStart(2, '0')}-01`, 'BS');
  }

  private async openBsDatePicker(): Promise<void> {
    this.bsCalendarOpen = true;
    const currentDate = this.getSelectedDateValue() || this.getSelectedDateFormValue();
    const currentKey = this.getSelectedDateValue() === this.selectedDateAdValue ? 'AD' : 'BS';
    await this.loadBsCalendar(currentDate, currentKey);
  }

  private async loadBsCalendar(bsDateValue: string, bsAdKey: 'AD' | 'BS' = 'BS'): Promise<void> {
    await loadReportBsCalendar({
      dateValue: bsDateValue,
      requestCalendar: () => firstValueFrom(this.httpSvc.requestCall(
        AuthEndPoints.GET_LOG_SHEET_BS_CALENDAR,
        ApiMethod.POST,
        { data_date: bsDateValue, bs_ad_key: bsAdKey },
        false,
        { suppressToast: true },
      )),
      extractCalendar: (response) => extractReportBsCalendar(response),
      setLoading: (loading) => {
        this.bsCalendarLoading = loading;
      },
      setCalendar: (calendar) => {
        this.bsCalendarMonth = calendar;
      },
      detectChanges: () => this.cdr.detectChanges(),
    });
  }

  private async handleDateSelectionChange(preserveViewState = false): Promise<void> {
    await handleReportDateSelection({
      adDate: this.getSelectedDateFormValue(),
      showBsDate: this.preferencesSvc.showBsDate,
      convertDate: (date) => firstValueFrom(this.httpSvc.requestCall(
        AuthEndPoints.CONVERT_LOG_SHEET_DATE,
        ApiMethod.POST,
        { data_date: date, bs_ad_key: 'BS' },
        false,
        { suppressToast: true },
      )).then((response) => extractReportDateConversion(response)),
      formatDate: (date) => this.preferencesSvc.formatDateOnly(date),
      fallbackDate: getTodayDateValue(),
      setAdDate: (date) => {
        this.selectedDateAdValue = date;
      },
      setSelection: (requestValue, displayLabel) => {
        this.selectedDateRequestValue = requestValue;
        this.selectedDateDisplayLabel = displayLabel;
      },
      loadReport: (preserve) => this.loadReport(preserve),
      preserveViewState,
    });
  }

}
